import React from "react";
import { AppConfigContext } from "../app/appConfig";
import { useAuth } from "./auth";
import type { User } from "../../api/auth/Auth.typed";

interface FetchHeadersProviderProps {
  children: React.ReactNode;
}

export function FetchHeadersProvider(props: FetchHeadersProviderProps) {
  const appConfig = React.useContext(AppConfigContext);
  const { user } = useAuth();
  const userRef = React.useRef(user);
  userRef.current = user;
  const [patched, setPatched] = React.useState(false);

  React.useEffect(() => {
    if (!appConfig?.zekayAPI) {
      setPatched(true);
      return;
    }

    const originalFetch =
      (window as Window & { __origFetch?: typeof fetch }).__origFetch || window.fetch;
    (window as Window & { __origFetch?: typeof fetch }).__origFetch = originalFetch;

    const backendOrigin = new URL(appConfig.zekayAPI).origin;

    window.fetch = (input: RequestInfo | URL, init: RequestInit = {}) => {
      const urlString =
        typeof input === "string" || input instanceof URL
          ? input.toString()
          : (input as Request).url;
      const requestOrigin = new URL(urlString, window.location.href).origin;
      const isBackend = requestOrigin === backendOrigin;
      const isAuthRoute = urlString.includes("/auth");

      const headers = new Headers(init.headers || {});
      if (isBackend && !isAuthRoute) {
        const u = userRef.current as (User & { Role?: { company_id?: number } }) | undefined;
        const companyId = u?.Role?.company_id ?? u?.company_id;
        if (companyId != null) headers.set("X-Company-ID", String(companyId));
      }

      return originalFetch(input, { ...init, headers });
    };

    setPatched(true);

    return () => {
      window.fetch = originalFetch;
    };
  }, [appConfig]);

  return patched ? <>{props.children}</> : null;
}
